import { Color, Spacing } from '@persona-id/design-tokens';
import { EditIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { Fragment, useCallback } from 'react';
import { WrappedFieldProps } from 'redux-form';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { NormalizationMethod, NormalizationMethodType } from 'dashboard/models/verification-check';
import Button from 'design-system/components/Button';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import useToggle from 'lib/hooks/useToggle';

const CONFIGURABLE_NORMALIZATION_METHOD_TYPES = [
  NormalizationMethodType.AbbreviateStreetSuffix,
  NormalizationMethodType.AbbreviateStreetUnit,
  NormalizationMethodType.AbbreviateSubdivision,
  NormalizationMethodType.CanonicalizeEmailAddress,
  NormalizationMethodType.ExpandCityAbbreviation,
  NormalizationMethodType.ExpandCitySuffix,
  NormalizationMethodType.ExpandStreetSuffix,
  NormalizationMethodType.ExpandStreetUnit,
  NormalizationMethodType.ExpandSubdivision,
  NormalizationMethodType.ExtractBusinessName,
  NormalizationMethodType.ExtractLetters,
  NormalizationMethodType.ExtractNumbers,
  NormalizationMethodType.ExtractRegistrationNumber,
  NormalizationMethodType.ExtractStreet,
  NormalizationMethodType.ExtractStreetName,
  NormalizationMethodType.ExtractStreetNumber,
  NormalizationMethodType.ExtractStreetType,
  NormalizationMethodType.ExtractStreetUnit,
  NormalizationMethodType.ExtractSubstring,
  NormalizationMethodType.FilterCharacters,
  NormalizationMethodType.FoldCharacters,
  NormalizationMethodType.LowercaseLetters,
  NormalizationMethodType.NormalizeCjkFullwidthCharacters,
  NormalizationMethodType.RemoveBusinessNameSuffix,
  NormalizationMethodType.RemoveCharacters,
  NormalizationMethodType.RemoveLetters,
  NormalizationMethodType.RemoveNumbers,
  NormalizationMethodType.RemovePrefixes,
  NormalizationMethodType.RemoveSpecialCharacters,
  NormalizationMethodType.RemoveStreetSuffix,
  NormalizationMethodType.RemoveSuffixes,
  NormalizationMethodType.RemoveWhitespaces,
  NormalizationMethodType.ReplaceCharacters,
  NormalizationMethodType.Titleize,
  NormalizationMethodType.Transliterate,
  NormalizationMethodType.UppercaseLetters,
];

const DEFAULT_NORMALIZATION_METHOD_SETTINGS = Object.fromEntries(
  CONFIGURABLE_NORMALIZATION_METHOD_TYPES.map((type) => [type, { type }]),
) as Record<Partial<NormalizationMethodType>, NormalizationMethod>;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 15% 85%;
`;

const StyledParagraph = styled(Paragraph)`
  padding-top: 13px;
  padding-bottom: ${Spacing[2]};
  padding-left: ${Spacing[1]};
  font-size: 13px;
  line-height: ${Spacing[4]};
`;

const RulesContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]} ${Spacing[2]} ${Spacing[4]};
  display: flex;
  flex-direction: column;
`;

const RuleContainer = styled.div`
  padding-top: ${Spacing[2]};
`;

const FieldPill = styled.span`
  background-color: ${Color.CoreSlate100};
  border-radius: ${BORDER_RADIUSES.rounded};
  display: inline-flex;
  width: fit-content;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  ${paragraphMixins.sm};
  margin: 2px;
  padding: 4px;
`;

const InputLabel = styled(Label).attrs(() => ({ size: 'md', weight: 'bold' }))`
  font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
`;

const EditRulesSection = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]} ${Spacing[4]};
`;

const Grid = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr 130px 1fr 32px;
  column-gap: ${Spacing[2]};
`;

const StyledButton = styled(Button)`
  background-color: ${Color.CoreSlate200};
  width: ${Spacing[7]};
  height: ${Spacing[5]};
`;

const CenteredIcon = styled.div`
  display: block;
  margin-top: auto;
  margin-bottom: auto;
`;

interface NormalizationMethodsSectionProps {
  editable: boolean;
}
const NormalizationMethodsSection = ({
  editable,
  input,
}: NormalizationMethodsSectionProps & WrappedFieldProps) => {
  const normalizationMethods = input.value as NormalizationMethod[];
  const [
    showNormalizationMethodEditor,
    { toggle: toggleNormalizationMethodEditor, disable: closeNormalizationMethodEditor },
  ] = useToggle(false);
  const [showNormalizationMethods, { set: setNormalizationMethodsExpanded }] = useToggle(false);
  const handleAddNormalizationMethod = useCallback(() => {
    const newNormalizationMethods = produce(normalizationMethods, (draftNormalizationMethods) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      draftNormalizationMethods.push({ type: null });
    });
    input.onChange(newNormalizationMethods);
  }, [normalizationMethods, input]);
  const handleUpdateNormalizationMethodType = useCallback(
    (index: number, type: NormalizationMethodType) => {
      const newNormalizationMethods = produce(normalizationMethods, (draftNormalizationMethods) => {
        draftNormalizationMethods[index] = DEFAULT_NORMALIZATION_METHOD_SETTINGS[type];
      });
      input.onChange(newNormalizationMethods);
    },
    [normalizationMethods, input],
  );
  const handleDeleteNormalizationMethod = useCallback(
    (index: number) => {
      const newNormalizationMethods = produce(normalizationMethods, (draftNormalizationMethods) => {
        draftNormalizationMethods.splice(index, 1);
      });
      input.onChange(newNormalizationMethods);
    },
    [normalizationMethods, input],
  );
  return (
    <Fragment>
      <HeadingContainer>
        <Accordion
          title={<InputLabel>Normalization Methods</InputLabel>}
          padded={false}
          disabled={!editable}
          onChange={setNormalizationMethodsExpanded}
          defaultExpanded={false}
        />
        {editable && (
          <Button
            variant='basic'
            width='icon'
            type='button'
            id='match-requirement-panel__edit-normalization-methods'
            onClick={toggleNormalizationMethodEditor}
          >
            <EditIcon />
          </Button>
        )}
      </HeadingContainer>
      <Spacer height={1} />
      {showNormalizationMethods && (
        <RulesContainer>
          {normalizationMethods.length > 0 ? (
            normalizationMethods.map((normalizationMethod, i) => (
              <GridRow key={i}>
                <StyledParagraph> {i === 0 ? 'Apply' : 'Then'}</StyledParagraph>
                <RuleContainer>
                  <NormalizationMethodConfig normalizationMethod={normalizationMethod} />
                </RuleContainer>
              </GridRow>
            ))
          ) : (
            <StyledParagraph>
              {editable
                ? 'Press the edit button to add normalization methods'
                : 'No normalization methods'}
            </StyledParagraph>
          )}
        </RulesContainer>
      )}
      <Spacer height={1} />
      {editable && showNormalizationMethodEditor && (
        <NormalizationMethodsEditor
          normalizationMethods={normalizationMethods}
          handleAddNormalizationMethod={handleAddNormalizationMethod}
          handleUpdateNormalizationMethodType={handleUpdateNormalizationMethodType}
          handleDeleteNormalizationMethod={handleDeleteNormalizationMethod}
          onClose={closeNormalizationMethodEditor}
        />
      )}
    </Fragment>
  );
};

interface NormalizationMethodConfigProps {
  normalizationMethod: NormalizationMethod;
}
const NormalizationMethodConfig = ({ normalizationMethod }: NormalizationMethodConfigProps) => {
  switch (normalizationMethod.type) {
    // Normalization method type would be null when we are adding a new normalization method
    case null:
      return <FieldPill> empty method</FieldPill>;
    default:
      return <FieldPill> {normalizationMethod.type}</FieldPill>;
  }
};

interface NormalizationMethodsEditorProps {
  normalizationMethods: NormalizationMethod[];
  handleAddNormalizationMethod: () => void;
  handleUpdateNormalizationMethodType: (index: number, type: NormalizationMethodType) => void;
  handleDeleteNormalizationMethod: (index: number) => void;
  onClose: () => void;
}
const NormalizationMethodsEditor = ({
  normalizationMethods,
  handleAddNormalizationMethod,
  handleUpdateNormalizationMethodType,
  handleDeleteNormalizationMethod,
  onClose,
}: NormalizationMethodsEditorProps) => {
  return (
    <MaximizablePanel
      title='Edit Normalization Methods'
      onOutsideClickAndEscPress={onClose}
      offsetElementId='match-requirement-panel__container'
    >
      <EditRulesSection>
        {normalizationMethods.length > 0 ? (
          normalizationMethods.map((normalizationMethod, i) => (
            <>
              <Spacer height={1} />
              <Label size='sm'>{i === 0 ? 'Apply' : 'Then'}</Label>
              <Spacer height={1} />
              <Grid key={i}>
                <Select
                  value={normalizationMethod.type}
                  onValueChange={(type) =>
                    handleUpdateNormalizationMethodType(i, type as NormalizationMethodType)
                  }
                  placeholder='Select type'
                >
                  {CONFIGURABLE_NORMALIZATION_METHOD_TYPES.map((normalizationMethodType) => (
                    <Option key={normalizationMethodType} value={normalizationMethodType}>
                      {normalizationMethodType}
                    </Option>
                  ))}
                </Select>
                {normalizationMethod.type && (
                  <NormalizationMethodsConfigEditor normalizationMethod={normalizationMethod} />
                )}
                <CenteredIcon>
                  <Button
                    id='multi-input__value-remove'
                    variant='basic'
                    width='icon'
                    onClick={() => handleDeleteNormalizationMethod(i)}
                  >
                    <TrashIcon />
                  </Button>
                </CenteredIcon>
              </Grid>
            </>
          ))
        ) : (
          <StyledParagraph> Press &quot;Then&quot; to add a normalization method</StyledParagraph>
        )}
        <Spacer height={2} />
        <StyledButton
          onClick={handleAddNormalizationMethod}
          id='match-requirement-panel__add-normalization-method'
          type='button'
          variant='basic'
          width='icon'
        >
          Then
        </StyledButton>
      </EditRulesSection>
    </MaximizablePanel>
  );
};

interface NormalizationMethodsConfigEditorProps {
  normalizationMethod: NormalizationMethod;
}
const NormalizationMethodsConfigEditor = ({
  normalizationMethod,
}: NormalizationMethodsConfigEditorProps) => {
  // TODO: add configuration to normalization methods
  switch (normalizationMethod.type) {
    default:
      return <></>;
  }
};

export default NormalizationMethodsSection;
